import { Search } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useGlobalSearchQuery } from '../../api/search/searchApiSlice';
import { SEARCH_MIN_LENGTH, SearchGroupKind, SearchHit } from '../../api/search/searchApiTypes';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { getApiErrorMessage } from '../../utils/apiError';
import { cn } from '../../utils/utils';
import { searchResultPath } from './searchResultPath';

/** How long typing must pause before a request goes out. */
export const SEARCH_DEBOUNCE_MS = 250;

interface FlatHit extends SearchHit {
    kind: SearchGroupKind;
    path: string;
}

interface CommandPaletteProps {
    onClose: () => void;
}

/**
 * The ⌘K / Ctrl+K palette (#3016). Four states are kept apart on screen,
 * because a failure must never look like "no results": the input is too short,
 * a search is running, the search failed (with the server's reason and a
 * retry), and the search ran and matched nothing.
 */
const CommandPalette = ({ onClose }: CommandPaletteProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [term, setTerm] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const optionRefs = useRef<Array<HTMLLIElement | null>>([]);

    const trimmed = term.trim();
    const debounced = useDebouncedValue(trimmed, SEARCH_DEBOUNCE_MS);
    const tooShort = trimmed.length < SEARCH_MIN_LENGTH;
    const { currentData, isFetching, isError, error, refetch } = useGlobalSearchQuery(
        { q: debounced },
        { skip: debounced.length < SEARCH_MIN_LENGTH },
    );
    const pending = !tooShort && (debounced !== trimmed || isFetching);

    const groups = useMemo(() => (tooShort || pending ? [] : currentData?.groups ?? []), [currentData, tooShort, pending]);
    const flat = useMemo<FlatHit[]>(
        () => groups.flatMap((group) => group.items.map((item) => ({
            ...item,
            kind: group.kind,
            path: searchResultPath(group.kind, item.id),
        }))),
        [groups],
    );

    // A new answer starts the selection at its first hit.
    useEffect(() => {
        setActiveIndex(0);
    }, [flat]);

    useEffect(() => {
        optionRefs.current[activeIndex]?.scrollIntoView?.({ block: 'nearest' });
    }, [activeIndex]);

    // Keyboard users return to where they were when the palette closes.
    useEffect(() => {
        const previous = document.activeElement as HTMLElement | null;
        inputRef.current?.focus();
        return () => previous?.focus?.();
    }, []);

    const openHit = (hit: FlatHit) => {
        onClose();
        navigate(hit.path);
    };

    const onKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            onClose();
            return;
        }
        if (flat.length === 0) return;
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveIndex((index) => (index + 1) % flat.length);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex((index) => (index - 1 + flat.length) % flat.length);
        } else if (event.key === 'Enter') {
            event.preventDefault();
            const hit = flat[activeIndex];
            if (hit) openHit(hit);
        }
    };

    const renderStatus = () => {
        if (tooShort) {
            return <p className={'px-4 py-6 text-sm text-foreground/60'}>{t('global_search.too_short', { min: SEARCH_MIN_LENGTH })}</p>;
        }
        if (pending) {
            return <p className={'px-4 py-6 text-sm text-foreground/60'} role={'status'}>{t('global_search.loading')}</p>;
        }
        if (isError) {
            const reason = getApiErrorMessage(error);
            return (
                <div className={'px-4 py-6 text-sm text-foreground'} role={'alert'}>
                    <p className={'font-medium'}>{t('global_search.error')}</p>
                    {reason && <p className={'mt-1 text-foreground/60'}>{reason}</p>}
                    <button type={'button'} onClick={() => refetch()} className={'mt-2 text-primary hover:underline'}>
                        {t('global_search.retry')}
                    </button>
                </div>
            );
        }
        if (!currentData) return null;
        if (currentData.groups.length === 0) {
            return <p className={'px-4 py-6 text-sm text-foreground/60'}>{t('global_search.nothing_searchable')}</p>;
        }
        if (flat.length === 0) {
            return <p className={'px-4 py-6 text-sm text-foreground/60'}>{t('global_search.no_results', { query: trimmed })}</p>;
        }
        return null;
    };

    let offset = 0;
    const activeId = flat[activeIndex] ? `global-search-option-${activeIndex}` : undefined;

    return (
        <div className={'fixed inset-0 z-[1300] flex items-start justify-center bg-foreground/20 px-4 pt-[12vh]'} onMouseDown={onClose}>
            <div
                role={'dialog'}
                aria-modal={'true'}
                aria-label={t('global_search.title')}
                className={'w-full max-w-xl overflow-hidden rounded-[5px] bg-text-contrast shadow-lg'}
                onMouseDown={(event) => event.stopPropagation()}
            >
                <div className={'flex items-center gap-2 border-b border-foreground/5 px-4'}>
                    <Search className={'h-4 w-4 shrink-0 text-foreground/60'} />
                    <input
                        ref={inputRef}
                        type={'text'}
                        role={'combobox'}
                        aria-expanded={flat.length > 0}
                        aria-controls={'global-search-results'}
                        aria-activedescendant={activeId}
                        aria-autocomplete={'list'}
                        value={term}
                        onChange={(event) => setTerm(event.target.value)}
                        onKeyDown={onKeyDown}
                        placeholder={t('global_search.placeholder')}
                        className={'w-full bg-transparent py-3 text-sm text-foreground placeholder:text-foreground/50 focus:outline-none'}
                    />
                    <kbd className={'text-xs font-sans text-foreground/50'}>Esc</kbd>
                </div>
                <div className={'max-h-[60vh] overflow-y-auto'}>
                    {renderStatus()}
                    {flat.length > 0 && (
                        <ul id={'global-search-results'} role={'listbox'} aria-label={t('global_search.title')} className={'py-2'}>
                            {groups.filter((group) => group.items.length > 0).map((group) => {
                                const start = offset;
                                offset += group.items.length;
                                return (
                                    <li key={group.kind} role={'presentation'}>
                                        <p className={'px-4 pb-1 pt-2 text-xs font-medium text-foreground/60'}>
                                            {t(`global_search.groups.${group.kind}`)}
                                        </p>
                                        <ul role={'group'} aria-label={t(`global_search.groups.${group.kind}`)}>
                                            {group.items.map((item, i) => {
                                                const index = start + i;
                                                const hit = flat[index];
                                                const active = index === activeIndex;
                                                return (
                                                    <li
                                                        key={item.id}
                                                        id={`global-search-option-${index}`}
                                                        ref={(el) => { optionRefs.current[index] = el; }}
                                                        role={'option'}
                                                        aria-selected={active}
                                                        onMouseEnter={() => setActiveIndex(index)}
                                                        onClick={() => hit && openHit(hit)}
                                                        className={cn(
                                                            'mx-2 cursor-pointer rounded-[5px] px-2 py-2 text-sm',
                                                            active ? 'bg-primary text-text-buttonContrast' : 'text-foreground',
                                                        )}
                                                    >
                                                        <span className={'block truncate font-medium'}>{item.title || t('common.not_applicable')}</span>
                                                        {item.subtitle && (
                                                            <span className={cn('block truncate text-xs', active ? 'opacity-80' : 'text-foreground/60')}>
                                                                {item.subtitle}
                                                            </span>
                                                        )}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CommandPalette;
